import { useState, useEffect } from 'react'
import {
  FileText,
  CheckCircle,
  XCircle,
  Info,
  Clock,
  Inbox,
  Filter,
  Ban,
} from 'lucide-react'
import { useEmployeeContext } from './EmployeeDetailLayout'
import { notificationService } from '../../services/notificationService'
import { Notification, NotificationType } from '../../types'
import { formatDate } from '../../utils/dateUtils'

const notificationIcons: Record<NotificationType, React.ComponentType<any>> = {
  NEW_NOTE: FileText,
  NOTE_UPDATED: FileText,
  REQUEST_APPROVED: CheckCircle,
  REQUEST_DENIED: XCircle,
  REQUEST_CANCELLED: Ban,
  SYSTEM: Info,
}

const notificationColors: Record<NotificationType, string> = {
  NEW_NOTE: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  NOTE_UPDATED: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  REQUEST_APPROVED: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  REQUEST_DENIED: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  REQUEST_CANCELLED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  SYSTEM: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
}

const notificationTypeLabels: Record<NotificationType, string> = {
  NEW_NOTE: 'Notas nuevas',
  NOTE_UPDATED: 'Notas actualizadas',
  REQUEST_APPROVED: 'Solicitudes aprobadas',
  REQUEST_DENIED: 'Solicitudes denegadas',
  REQUEST_CANCELLED: 'Solicitudes canceladas',
  SYSTEM: 'Sistema',
}

type FilterType = 'all' | 'unread' | NotificationType

export function EmployeeNotificationsTab() {
  const { employee } = useEmployeeContext()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<FilterType>('all')

  useEffect(() => {
    if (employee) {
      fetchNotifications()
    }
  }, [employee])

  const fetchNotifications = async () => {
    if (!employee) return
    setIsLoading(true)
    try {
      const data = await notificationService.getByEmployeeId(employee.id)
      setNotifications(data)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!employee) return null

  const formatFullDate = (dateStr: string) => {
    return formatDate(dateStr)
  }

  // Filter notifications
  const filteredNotifications = notifications.filter((n) => {
    if (filter === 'all') return true
    if (filter === 'unread') return !n.isRead
    return n.type === filter
  })

  const unreadCount = notifications.filter((n) => !n.isRead).length

  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce((groups, notification) => {
    const date = formatDate(notification.createdAt)
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(notification)
    return groups
  }, {} as Record<string, Notification[]>)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Historial de Notificaciones
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {notifications.length} notificacion{notifications.length !== 1 ? 'es' : ''} en total
            {unreadCount > 0 && (
              <span className="ml-2 text-blue-600 dark:text-blue-400">
                ({unreadCount} sin leer)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtrar por:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-400 dark:hover:bg-slate-600'
            }`}
          >
            Todas
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filter === 'unread'
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-400 dark:hover:bg-slate-600'
            }`}
          >
            Sin leer ({unreadCount})
          </button>
          {(Object.keys(notificationTypeLabels) as NotificationType[]).map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                filter === type
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-400 dark:hover:bg-slate-600'
              }`}
            >
              {notificationTypeLabels[type]}
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-200 border-t-primary-600"></div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="py-16 text-center">
            <Inbox className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {filter === 'all'
                ? 'Este empleado no tiene notificaciones'
                : 'No hay notificaciones con este filtro'}
            </p>
          </div>
        ) : (
          <div>
            {Object.entries(groupedNotifications).map(([date, dateNotifications]) => (
              <div key={date}>
                <div className="px-4 py-2 bg-gray-50 dark:bg-slate-900/50 border-b border-gray-200 dark:border-slate-700">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 capitalize">
                    {date}
                  </span>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-slate-700">
                  {dateNotifications.map((notification) => {
                    const Icon = notificationIcons[notification.type]
                    const colorClass = notificationColors[notification.type]

                    return (
                      <div
                        key={notification.id}
                        className={`p-4 ${
                          !notification.isRead ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                        }`}
                      >
                        <div className="flex gap-4">
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${colorClass} flex items-center justify-center`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm text-gray-900 dark:text-white ${!notification.isRead ? 'font-semibold' : 'font-medium'}`}>
                                {notification.title}
                              </p>
                              {!notification.isRead && (
                                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatFullDate(notification.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
